import { APP_ASSETS } from "@/types/supported-venue";
import { isUseLocalModel } from "@/utils/link-detection";
import * as turf from "@turf/turf";

interface ILatLngPair {
  lat: number;
  lng: number;
}
interface ISizePair {
  width: number;
  height: number;
}
interface MapData {
  id: string;
  name: string;
  level: number;
}

export interface IMapAlign {
  name: string;
  projectId?: number;
  venueId?: string;
  entry_mapId?: number;
  i18nVenue: string;
  venueName?: string;
  venueName_zh?: string;
  imageUrl: string;
  pixInMeter: number;
  center: ILatLngPair;
  angle: number;
  scale: number;
  corners: {
    tl: ILatLngPair;
    tr: ILatLngPair;
    br: ILatLngPair;
    bl: ILatLngPair;
  };
  size: ISizePair;
  sizeInMeters: ISizePair;
  venue?: string;
  polygon?: any;
  mapData?: MapData[];
  modelConfig?: VenueModelConfig;
}

export type VenueModelConfig = {
  folderPath: string;
  defaultPosition: number[];
  scaleFactor: number;
  moveSpeed: number;
  rotateSpeed: number;
  revealSpeed?: number;
  defaultFloorIndex: number;
  topAltitude: number;
  downSpeed: number;
  lod?: string;
  mainSplat?: string;
  floorMesh?: string;
  floorMeshPosition?: number[];
  floor?: Array<VenueModelFloor>;
  mesh?: meshSetting;
};

export type VenueModelFloor = {
  index: number;
  name: string;
  sog?: string;
  mesh?: MeshModel;
  altitude: number;
  cameras?: number[][];
};

export type meshSetting = {
  meshModels: MeshModel[];
};

export type MeshModel = {
  index: number;
  url: string;
  position: number[];
  rotationAngle: number[];
  scale: number[];
  color?: string;
  opacity?: number;
  rotateObjects?: string[];
};

export const calculateXy = (mapAlign: IMapAlign, lat: number, lng: number) => {
  const target = turf.point([lng, lat]);
  const coordTL = mapAlign["corners"]["tl"];
  const coordTR = mapAlign["corners"]["tr"];
  const pointTL = turf.point([coordTL["lng"], coordTL["lat"]]);
  const pointTR = turf.point([coordTR["lng"], coordTR["lat"]]);

  const distanceTlToTarget = turf.distance(pointTL, target, {
    units: "meters",
  });
  const bearingTlToTarget = turf.bearing(pointTL, target);
  const bearingTlToTr = turf.bearing(pointTL, pointTR);
  const pxDiagonal = Math.sqrt(
    mapAlign["size"]["width"] ** 2 + mapAlign["size"]["height"] ** 2,
  );
  const meterDiagonal = Math.sqrt(
    mapAlign["sizeInMeters"]["width"] ** 2 +
      mapAlign["sizeInMeters"]["height"] ** 2,
  );
  const pxInMeter = pxDiagonal / meterDiagonal;
  const pxDistance = pxInMeter * distanceTlToTarget;

  const bearingOffset = bearingTlToTarget - (bearingTlToTr - 90);
  const angleTlToTarget = bearingOffset * (Math.PI / 180);
  const xOffset = pxDistance * Math.sin(angleTlToTarget);
  const yOffset = pxDistance * Math.cos(angleTlToTarget);
  return { x: xOffset, y: -1 * yOffset };
};

const backupInfo = [
  { id: 3, name: "Art Park", name_zh: "藝術公園" },
  { id: 4, name: "Freespace", name_zh: "自由空間" },
  { id: 5, name: "Hong Kong Palace Museum", name_zh: "香港故宮文化博物館" },
  { id: 6, name: "M+", name_zh: "M+" },
  { id: 7, name: "Xiqu Centre", name_zh: "戲曲中心" },
];

// const WKCD_MAP_ALIGN: IMapAlign[] = [
//     {"venue":"Xiqu","name":"L0","imageUrl":"https://locaite-dev.collectiv.co/assets/50beb999-6abd-47d1-960d-16d65aaf56ab/m2-1718339315327ZERALU.png","pixInMeter":19.397799726963633,"center":{"lat":22.30216765875302,"lng":114.16723631100545},"angle":356.1234534144261,"scale":51.55228702359372,"corners":{"tl":{"lat":22.30271961208479,"lng":114.1665529985258},"tr":{"lat":22.30280349838915,"lng":114.1678294698037},"br":{"lat":22.30161570542125,"lng":114.1679196234851},"bl":{"lat":22.30153181911689,"lng":114.1666431522071}},"size":{"width":2560,"height":2560},"sizeInMeters":{"width":131.6500605703039,"height":132.4019818772277}},
//     {"venue":"M+","name":"L0","imageUrl":"https://locaite-dev.collectiv.co/assets/adec446e-8ad3-49a4-bd90-fed5f442ebbd/m2-1718707281081CEFOFU.png","pixInMeter":20.747134679934007,"center":{"lat":22.300853443913553,"lng":114.15912420169451},"angle":356.2152582177846,"scale":48.19950495769161,"corners":{"tl":{"lat":22.301680646033,"lng":114.1581034463103},"tr":{"lat":22.30180328806959,"lng":114.1600131494735},"br":{"lat":22.30002624179411,"lng":114.1601449570787},"bl":{"lat":22.29990359975752,"lng":114.1582352539155}},"size":{"width":4096,"height":4096},"sizeInMeters":{"width":196.93818859815383,"height":198.06353462167613}},
//     {"venue":"HKPM","name":"L0","imageUrl":"https://locaite-dev.collectiv.co/assets/c2461d0c-c362-4236-aaa8-0ce4e3f5b046/m1-1718702095995REHAYI.png","pixInMeter":20.8194792310068,"center":{"lat":22.301620666732227,"lng":114.1554200710521},"angle":358.97274743891023,"scale":48.03202207611783,"corners":{"tl":{"lat":22.30248931633708,"lng":114.1544468985085},"tr":{"lat":22.30252622803213,"lng":114.1563535646936},"br":{"lat":22.30075201712738,"lng":114.1563932435957},"bl":{"lat":22.30071510543232,"lng":114.1544865774106}},"size":{"width":4096,"height":4096},"sizeInMeters":{"width":196.1948851364027,"height":197.32575120636898}}
// ];

//Extra manuel custom data
const WKCD_MAP_ALIGN: IMapAlign[] = [
  {
    venue: "foodexpo2026",
    venueId: "2313693d-7aee-43e5-9d3c-4fc22bcad15a",
    i18nVenue: "food_expo_2026",
    venueName: "HKCEC Hall 1ABCD",
    venueName_zh: "HKCEC Hall 1ABCD",
    name: "L0",
    imageUrl:
      "https://locaite-dev.collectiv.co/assets/ebba263b-e3a9-491a-924a-ff1da609a51f/m13-1774536795904SOSOXU.jpg",
    pixInMeter: 10.83130246437555,
    center: {
      lat: 22.28308785302944,
      lng: 114.17297960082531,
    },
    angle: 86.2,
    scale: 92.325,
    corners: {
      tl: {
        lat: 22.28401928372865,
        lng: 114.17324143156486,
      },
      tr: {
        lat: 22.282196647107835,
        lng: 114.1733722589604,
      },
      br: {
        lat: 22.28215642035228,
        lng: 114.17271777197197,
      },
      bl: {
        lat: 22.283979060929028,
        lng: 114.17258694004545,
      },
    },
    size: {
      width: 2200,
      height: 731,
    },
    sizeInMeters: {
      width: 203.1147649628199,
      height: 67.48979493224243,
    },
    mapData: [{ id: "L1", name: "L1", level: 1 }],
    modelConfig: {
      folderPath: "venue/food_expo_2026/",
      defaultPosition: [-1100, 25, -365.5],
      defaultFloorIndex: 1,
      topAltitude: 300,
      downSpeed: 200,
      scaleFactor: 16.2469536962,
      moveSpeed: 2,
      rotateSpeed: 20,
      revealSpeed: 30,
      lod: "lod_20260409_1200/lod-meta.json",
      mesh: {
        meshModels: [
          // {
          //   index: 0,
          //   url: "extra_mesh/all_without_signage_20260402_1830.glb",
          //   position: [-1100, 0, -365.5],
          //   rotationAngle: [0, 180, 0],
          //   scale: [455, 455, 455],
          //   color: "#FFFFFF",
          //   opacity: 1,
          // },
          {
            index: 1,
            url: "extra_mesh/signage_20260404_1715.glb",
            position: [-1100, 0, -365.5],
            rotationAngle: [0, 180, 0],
            scale: [455, 455, 455],
            color: "#FFFFFF",
            opacity: 1,
            rotateObjects: [
              "signage_ageing",
              "signage_cheung_chau",
              "signage_first_aid",
              "signage_food_fun",
              "signage_gold_pig",
              "signage_jazz",
              "signage_lucky_draw_1",
              "signage_lucky_draw_2",
              "signage_lucky_draw_3",
              "signage_main_stage",
              "signage_market",
              "signage_redemp_1",
              "signage_redemp_2",
              "signage_silk_road",
              "signage_vintage",
              "signage_whimsy",
            ],
          },
        ],
      },
      floor: [
        {
          index: 1,
          name: "L1",
          mesh: {
            index: 0,
            url: "extra_mesh/all_without_signage_20260402_1830.glb",
            position: [-1100, 0, -365.5],
            rotationAngle: [0, 180, 0],
            scale: [455, 455, 455],
            color: "#FFFFFF",
            opacity: 1,
          },
          altitude: 25,
        },
      ],
    },
  },
  {
    venue: "Xiqu",
    venueId: "227ba8d6-fc31-4c54-9690-c4a1659ee180",
    i18nVenue: "venue_xiqu_centre",
    venueName: "Xiqu Centre",
    venueName_zh: "戲曲中心",
    name: "L0",
    imageUrl:
      "https://locaite-dev.collectiv.co/assets/50beb999-6abd-47d1-960d-16d65aaf56ab/m2-1718339315327ZERALU.png",
    pixInMeter: 19.397799726963633,
    center: { lat: 22.30216765875302, lng: 114.16723631100545 },
    angle: 356.1234534144261,
    scale: 51.55228702359372,
    corners: {
      tl: { lat: 22.30271961208479, lng: 114.1665529985258 },
      tr: { lat: 22.30280349838915, lng: 114.1678294698037 },
      br: { lat: 22.30161570542125, lng: 114.1679196234851 },
      bl: { lat: 22.30153181911689, lng: 114.1666431522071 },
    },
    size: { width: 2560, height: 2560 },
    sizeInMeters: { width: 131.6500605703039, height: 132.4019818772277 },
    entry_mapId: 418,
    mapData: [
      { id: "416", name: "B2", level: -2 },
      { id: "417", name: "LG", level: -1 },
      { id: "418", name: "G", level: 0 },
      { id: "419", name: "L1", level: 1 },
      { id: "420", name: "L2", level: 2 },
    ],
    modelConfig: {
      folderPath: "venue/xiqu/",
      defaultPosition: [-1383, 0, -1415],
      defaultFloorIndex: 0,
      topAltitude: 600,
      downSpeed: 200,
      scaleFactor: 29.0966722643,
      moveSpeed: 2,
      rotateSpeed: 20,
      lod: "lod/lod-meta.json",
      floor: [
        {
          index: -2,
          name: "B2",
          sog: "b2/xiqu_b2_20260119_1630.sog",
          altitude: -185,
          cameras: [
            [-1983.269, -203.1517, 977.2491],
            [-1984.88, -196.5339, 531.597],
            [-2166.221, -196.5, 748.2565],
          ],
        },
        {
          index: -1,
          name: "LG",
          sog: "lg/xiqu_lg_20260119_1630.sog",
          altitude: -55,
          cameras: [
            [-1950.242, -51.84412, 829.118],
            [-1692.008, -43.31032, 1957.18],
            [-1929.422, -28.37321, 1407.86],
            [-1793.617, -32.18947, 1133.987],
            [-1662.391, -32.18947, 1038.885],
            [-1923.973, -36.09593, 1355.136],
            [-1492.856, -2.235001, 1852.17],
          ],
        },
        {
          index: 0,
          name: "L0",
          sog: "l0/xiqu_l0_20260119_1630.sog",
          altitude: 0,
          cameras: [[-1496.303, 64.14569, 1594.011]],
        },
        {
          index: 1,
          name: "L1",
          sog: "l1/xiqu_l1_20260119_1630.sog",
          altitude: 110,
          cameras: [
            [-1088.505, 114.4794, 1794.438],
            [-1349.384, 142.89, 1359.756],
            [-1792.78, 102.12, 1611.272],
          ],
        },
        {
          index: 2,
          name: "L2",
          sog: "l2/xiqu_l2_20260119_1630.sog",
          altitude: 230,
          cameras: [
            [-1187.912, 255.7549, 1942.846],
            [-1274.9, 214.845, 1553.807],
            [-475.7089, 269.79, 1831.552],
            [-1711.162, 216.33, 1529.863],
          ],
        },
      ],
    },
  },
  {
    venue: "M+",
    venueId: "M+",
    i18nVenue: "venue_m_plus",
    venueName: "M+",
    venueName_zh: "M+",
    name: "L0",
    imageUrl:
      "https://locaite-dev.collectiv.co/assets/adec446e-8ad3-49a4-bd90-fed5f442ebbd/m2-1718707281081CEFOFU.png",
    pixInMeter: 20.747134679934007,
    center: { lat: 22.300853443913553, lng: 114.15912420169451 },
    angle: 356.2152582177846,
    scale: 48.19950495769161,
    corners: {
      tl: { lat: 22.301680646033, lng: 114.1581034463103 },
      tr: { lat: 22.30180328806959, lng: 114.1600131494735 },
      br: { lat: 22.30002624179411, lng: 114.1601449570787 },
      bl: { lat: 22.29990359975752, lng: 114.1582352539155 },
    },
    size: { width: 4096, height: 4096 },
    sizeInMeters: { width: 196.93818859815383, height: 198.06353462167613 },
    entry_mapId: 0,
  },
  {
    venue: "HKPM",
    venueId: "HKPM",
    i18nVenue: "venue_palace_museum",
    venueName: "Hong Kong Palace Museum",
    venueName_zh: "香港故宮文化博物館",
    name: "L0",
    imageUrl:
      "https://locaite-dev.collectiv.co/assets/c2461d0c-c362-4236-aaa8-0ce4e3f5b046/m1-1718702095995REHAYI.png",
    pixInMeter: 20.8194792310068,
    center: { lat: 22.301620666732227, lng: 114.1554200710521 },
    angle: 358.97274743891023,
    scale: 48.03202207611783,
    corners: {
      tl: { lat: 22.30248931633708, lng: 114.1544468985085 },
      tr: { lat: 22.30252622803213, lng: 114.1563535646936 },
      br: { lat: 22.30075201712738, lng: 114.1563932435957 },
      bl: { lat: 22.30071510543232, lng: 114.1544865774106 },
    },
    size: { width: 4096, height: 4096 },
    sizeInMeters: { width: 196.1948851364027, height: 197.32575120636898 },
    entry_mapId: 0,
  },
  {
    venue: "ELEMENTS",
    venueId: "elements",
    i18nVenue: "elements",
    venueName: "Elements",
    name: "",
    imageUrl: "",
    pixInMeter: 0,
    center: { lat: 0, lng: 0 },
    angle: 0,
    scale: 1,
    corners: {
      tl: { lat: 0, lng: 0 },
      tr: { lat: 0, lng: 0 },
      br: { lat: 0, lng: 0 },
      bl: { lat: 0, lng: 0 },
    },
    size: { width: 1, height: 1 },
    sizeInMeters: { width: 1, height: 1 },
    entry_mapId: 0,
    modelConfig: {
      folderPath: "venue/elements/",
      defaultPosition: [0, 21, 0],
      defaultFloorIndex: 0,
      topAltitude: 300,
      downSpeed: 200,
      scaleFactor: 20,
      moveSpeed: 2,
      rotateSpeed: 20,
      lod: "lod/lod-meta.json",
      floor: [
        {
          index: 0,
          name: "L0",
          sog: "l0/elements_l0_20260106_2100.sog",
          altitude: 21,
        },
        {
          index: 1,
          name: "L1",
          sog: "l1/elements_l1_20260106_2100.sog",
          altitude: 90,
        },
      ],
    },
  },
  {
    venue: "WestKowloonStation",
    venueId: "4aafeb4b-a570-45fd-8404-119906425dbd",
    i18nVenue: "venue_west_kowloon_station",
    venueName: "West Kowloon Station",
    venueName_zh: "西九龍站",
    name: "B1M",
    imageUrl:
      "https://locaite-dev.collectiv.co/assets/ebba263b-e3a9-491a-924a-ff1da609a51f/m10-1767755145122YUVEDE.png",
    pixInMeter: 2.95314946605999,
    center: { lat: 22.304101804899837, lng: 114.1651105327814 },
    angle: 0,
    scale: 338.62153334513505,
    corners: {
      tl: { lat: 22.30688824745072, lng: 114.162098756837 },
      tr: { lat: 22.30688824745072, lng: 114.16812230872578 },
      br: { lat: 22.30131536234897, lng: 114.16812230872578 },
      bl: { lat: 22.30131536234897, lng: 114.162098756837 },
    },
    size: { width: 1830, height: 1830 },
    sizeInMeters: { width: 619.6650427589744, height: 619.6774060212555 },
    entry_mapId: 429,
    mapData: [
      { id: "426", name: "L1", level: 2 },
      { id: "427", name: "GFM", level: 1 },
      { id: "428", name: "G", level: 0 },
      { id: "429", name: "B1M", level: -1 },
      { id: "430", name: "B1", level: -2 },
      { id: "431", name: "B2M", level: -3 },
      { id: "432", name: "B2", level: -4 },
    ],
    modelConfig: {
      folderPath: "venue/west_kowloon_station/",
      defaultPosition: [-950, -12, -800],
      defaultFloorIndex: -2,
      topAltitude: 210,
      downSpeed: 200,
      scaleFactor: 4.42972419734,
      moveSpeed: 2,
      rotateSpeed: 20,
      lod: "lod/lod-meta.json",
      floorMesh: "floor_mesh/wksFloor_20260113_1800.glb",
      floorMeshPosition: [-915, 0, -915],
      floor: [
        {
          index: -4,
          name: "B2",
          sog: "b2/b2_20260108_1730_no_ceiling.sog",
          altitude: -34,
          cameras: [
            [-1099.559, -29.80091, 755.7816],
            [-1050.917, -29.16991, 698.5759],
            [-1149.892, -32.72666, 667.2157],
            [-1092.739, -26.25463, 590.5302],
            [-1031.372, -30.22681, 490.3717],
            [-1105.5, -30.95352, 823.5452],
            [-1149.203, -29.37656, 890.3356],
            [-1177.411, -30.76905, 992.0888],
            [-1159.174, -32.41691, 1100.82],
            [-1178.036, -30.58092, 1184.431],
            [-1195.337, -32.71932, 1242.275],
            [-1163.084, -33.02246, 1269.683],
            [-1192.809, -33.19062, 1351.573],
            [-1180.71, -33.92375, 1401.908],
            [-1198.83, -32.62684, 1484.04],
          ],
        },
        {
          index: -2,
          name: "B1",
          sog: "b1/b1_20260108_1730_no_ceiling.sog",
          altitude: -13,
          cameras: [
            [-1086.069, -11.96336, 699.2535],
            [-1014.693, -6.400549, 721.4101],
            [-960.6052, -5.388933, 826.6296],
            [-865.8367, -8.111762, 775.0499],
            [-928.604, -11.4955, 984.3691],
            [-880.289, -9.976798, 1043.982],
            [-764.5458, -9.44786, 1065.968],
            [-966.2633, -12.48993, 1100.371],
            [-988.1818, -7.453878, 1131.725],
            [-1097.071, -6.954564, 1119.769],
          ],
        },
        {
          index: 0,
          name: "G",
          sog: "g/g_20260108_1730_no_ceiling.sog",
          altitude: 7,
          cameras: [
            [-1099.559, -29.80091, 755.7816],
            [-1103.038, 6.912609, 766.6795],
            [-1108.134, 7.956286, 836.0771],
            [-1158.058, 7.014514, 887.9686],
            [-1176.37, 6.165534, 956.2593],
            [-1186.885, 6.147374, 1018.824],
            [-1162.103, 8.871501, 1055.512],
          ],
        },
        {
          index: 1,
          name: "GFM",
          sog: "gfm/gfm_20260108_1730_no_ceiling.sog",
          altitude: 8,
          cameras: [
            [-1039.631, 9.290417, 624.3079],
            [-940.8594, 9.015102, 650.8076],
            [-935.3864, 9.038339, 745.2511],
            [-929.6434, 9.971678, 876.5971],
            [-820.9655, 9.522997, 962.4642],
            [-790.3508, 11.263, 1039.302],
            [-698.0084, 10.10951, 1061.088],
          ],
        },
        {
          index: 2,
          name: "L1",
          sog: "l1/l1_20260108_1730_no_ceiling.sog",
          altitude: 21,
          cameras: [
            [-721.0762, 23.35177, 1085.67],
            [-665.3782, 22.92555, 1078.322],
          ],
        },
      ],
    },
  },
];

const _findWkcdMapAlign = (point: any) => {
  for (let i = 0; i < WKCD_MAP_ALIGN.length; i++) {
    if (!WKCD_MAP_ALIGN[i]?.polygon) {
      WKCD_MAP_ALIGN[i].polygon = turf.polygon([
        [
          [WKCD_MAP_ALIGN[i].corners.tl.lng, WKCD_MAP_ALIGN[i].corners.tl.lat],
          [WKCD_MAP_ALIGN[i].corners.tr.lng, WKCD_MAP_ALIGN[i].corners.tr.lat],
          [WKCD_MAP_ALIGN[i].corners.br.lng, WKCD_MAP_ALIGN[i].corners.br.lat],
          [WKCD_MAP_ALIGN[i].corners.bl.lng, WKCD_MAP_ALIGN[i].corners.bl.lat],
          [WKCD_MAP_ALIGN[i].corners.tl.lng, WKCD_MAP_ALIGN[i].corners.tl.lat],
        ],
      ]);
    }
    if (turf.booleanPointInPolygon(point, WKCD_MAP_ALIGN[i].polygon)) {
      return WKCD_MAP_ALIGN[i];
    }
  }
  return undefined;
};

export const calculateWkcdXy = (lat: number, lng: number) => {
  const mAlign = _findWkcdMapAlign(turf.point([lng, lat]));
  if (!mAlign) {
    console.error("Failed to resolve WKCD venue from lat lng.");
    return undefined;
  }
  const result = calculateXy(mAlign, lat, lng);
  return { venue: mAlign.venue, ...result };
};

export const getVenue = (lat: number, lng: number) => {
  return _findWkcdMapAlign(turf.point([lng, lat]));
};

export const getVenueShortNameById = (venueId: string) => {
  return WKCD_MAP_ALIGN.find((map) => map.venueId === venueId)?.venue || "";
};

export const getVenueByShortName = (venue: string) => {
  return WKCD_MAP_ALIGN.find((map) => map.venue?.toLowerCase() === venue.toLowerCase());
};

export const getVenue18nName = (projectId: number) => {
  return (
    WKCD_MAP_ALIGN.find((map) => map.projectId === projectId)?.i18nVenue || ""
  );
};

export const getMapAlignById = (venueId: string): IMapAlign => {
  const mapAlign =
    WKCD_MAP_ALIGN.find((map) => map.venueId === venueId) || null;
  if (mapAlign) {
    return mapAlign;
  } else {
    return WKCD_MAP_ALIGN[0];
  }
};

// const venueMapIds = [
//   {
//     projectId: "149", //xiqu
//     mapIds: [
//       { id: 416, name: "B2", level: -2 },
//       { id: 417, name: "LG", level: -1 },
//       { id: 418, name: "G", level: 0 },
//       { id: 419, name: "L1", level: 1 },
//       { id: 420, name: "L2", level: 2 },
//     ],
//   },
//   {
//     projectId: "150", // WestKowloon Station
//     mapIds: [
//       { id: 426, name: "L1", level: 2 },
//       { id: 427, name: "GFM", level: 1 },
//       { id: 428, name: "G", level: 0 },
//       { id: 429, name: "B1M", level: -1 },
//       { id: 430, name: "B1", level: -2 },
//       { id: 431, name: "B2M", level: -3 },
//       { id: 432, name: "B2", level: -4 },
//     ],
//   },
// ];

// export const getVenueMapId = (venueId: string, level: string) => {
//   const venue = venueMapIds.find((v) => v.projectId === venueId);
//   const map = venue?.mapIds.find((map) => map.level.toString() === level);
//   return map?.id.toString();
// };

export const isVenueSupported = (venueCode: string) => {
  const venue = WKCD_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  return venue != null;
};

export const getVenueMapId = (
  venueId: string,
  level: string,
): string | undefined => {
  const venue = WKCD_MAP_ALIGN.find((v) => v.venueId === venueId);
  const map = venue?.mapData?.find((map) => map.level.toString() === level);
  return map?.id.toString();
};

export const getVenueModelConfigByVenueId = (
  venueId: string,
): VenueModelConfig | null => {
  // if (venueCode in venueModelConfig) {
  //   return venueModelConfig[venueCode];
  // } else {
  //   return venueModelConfig["xiqu"];
  // }
  const venue = WKCD_MAP_ALIGN.find((v) => v.venueId === venueId);
  const modelConfig = venue?.modelConfig;
  return modelConfig ?? null;
};

export const getVenueModelConfigByVenueCode = (
  venueCode: string,
): VenueModelConfig | null => {
  const venue = WKCD_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  const modelConfig = venue?.modelConfig;
  return modelConfig ?? null;
};

export const getVenueIdByVenueCode = (venueCode: string): string => {
  const venue = WKCD_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  return venue?.venueId ?? "";
};

export const getVenueNameByVenueCode = (
  venueCode: string,
): string => {
  const venue = WKCD_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  const venueName = venue?.venueName;
  return venueName ?? "";
};

export const getBaseUrl = () => {
  const BASE_URL = isUseLocalModel() ? "/" : `${APP_ASSETS}/`;
  return BASE_URL;
};

export const getAltitudeByFloorIndex = (
  venueModelConfig: VenueModelConfig | null,
  floorIndex: number,
) => {
  if (venueModelConfig == null || venueModelConfig.floor == null) {
    return 0;
  }
  const floorData = venueModelConfig.floor.find(
    (floor) => floor.index === floorIndex,
  );
  return floorData?.altitude ?? 0;
};

export const getAltitudeByFloorName = (
  venueModelConfig: VenueModelConfig | null,
  floorName: string,
) => {
  if (venueModelConfig == null || venueModelConfig.floor == null) {
    return "";
  }
  const floorData = venueModelConfig.floor.find(
    (floor) => floor.name === floorName,
  );
  return floorData?.altitude ?? 0;
};

export const getFloorNameByFloorIndex = (
  venueModelConfig: VenueModelConfig | null,
  floorIndex: number,
) => {
  if (venueModelConfig == null || venueModelConfig.floor == null) {
    return "";
  }
  const floorData = venueModelConfig.floor.find(
    (floor) => floor.index === floorIndex,
  );
  return floorData?.name ?? "";
};

export const getFloorIndexByFloorName = (
  venueModelConfig: VenueModelConfig | null,
  floorName: string,
) => {
  if (venueModelConfig == null || venueModelConfig.floor == null) {
    return 0;
  }
  const floorData = venueModelConfig.floor.find(
    (floor) => floor.name === floorName,
  );
  return floorData?.index ?? 0;
};
